import { Dataset, v4Dataset } from 'hooks/data/datasets/types';

import { isSameRelease } from './datasetRef';
import { CompareDataset, DatasetRef } from './types';

/** The release to fall back to when a payload omits its own version/system_model. */
type ReleaseFallback = { ver: string; system_model: string };

const releaseLabel = ({ version, systemModel }: DatasetRef) => `${version}-${systemModel}`;

/**
 * Shared Mixpanel properties for comparison events, so every call site reports the same shape.
 *
 * The releases are reported explicitly rather than relying on the global `version`/`system_model`
 * super properties: those come from the route, which for a comparison describes at most one of the two
 * datasets. `dataset_ids` stays numeric-only so existing funnels keep working.
 *
 * The same_* flags describe what is being compared, and are only included once every dataset has
 * resolved (on a shared link the metadata may still be loading).
 */
export const compareEventProps = (datasets: (CompareDataset | undefined)[]) => {
  const resolved = datasets.filter((dataset): dataset is CompareDataset => Boolean(dataset));
  const ids = resolved.map((dataset) => dataset.id);
  const canDescribeShape = resolved.length > 1 && resolved.length === datasets.length;

  const isSame = (pick: (dataset: CompareDataset) => string) =>
    resolved.every((dataset) => pick(dataset) === pick(resolved[0]));

  return {
    dataset_ids: ids,
    dataset_count: ids.length,
    dataset_releases: resolved.map(releaseLabel),
    is_cross_release: resolved.length > 1 && !resolved.every((dataset) => isSameRelease(dataset, resolved[0])),
    ...(canDescribeShape && {
      same_activity: isSame((dataset) => dataset.activityName),
      same_reference_product: isSame((dataset) => dataset.referenceProduct),
      same_geography: isSame((dataset) => dataset.geography),
    }),
  };
};

/**
 * Builds the display fields of a CompareDataset from a fetched (non-V4) Dataset.
 * `timePeriod`, `impactScore` and `activityType` are not part of the Dataset payload — callers
 * spread those in separately when available.
 *
 * `fallback` must be the release the dataset was *requested* from, not the route's: stamping the wrong
 * release here would send every later fetch for this dataset to a release where the id means
 * something else.
 */
export const datasetToCompareItem = (id: string, data: Dataset, { ver, system_model }: ReleaseFallback): CompareDataset => ({
  id,
  version: data.version || ver,
  systemModel: data.system_model || system_model,
  activityName: data.activity_name,
  referenceProduct: data.reference_product,
  geography: data.geography.short_name
    ? `${data.geography.long_name} (${data.geography.short_name})`
    : data.geography.long_name,
  unit: data.unit,
  sector: data.sector,
  hasAccess: data.has_access,
});

export const v4DatasetToCompareItem = (
  id: string,
  data: v4Dataset,
  { ver, system_model }: ReleaseFallback
): CompareDataset => ({
  id,
  version: data.version || ver,
  systemModel: data.system_model || system_model,
  activityName: data.name,
  referenceProduct: data.product,
  geography: data.geography_short ? `${data.geography} (${data.geography_short})` : data.geography,
  unit: data.unit,
  hasAccess: data.has_access,
});
